/**
 * Live microphone state for the mute button.
 *
 * Mute is a purely room-local toggle — no core round trip, no confirmation.
 * The track stays published; muting only stops the frames, which is why the
 * visualizer's local band drops to ~0 rather than disappearing.
 *
 * Web reads this through LiveKit's React context, which costs a
 * `RoomContext.Provider` around the whole tree. The RN widget already holds
 * the Room (the transport hands it back), so it subscribes directly — one
 * fewer provider for integrators to wire, and one fewer way to wire it wrong.
 */

import { useCallback, useEffect, useState } from "react";
import { RoomEvent, type Room } from "livekit-client";

export interface MicrophoneControl {
  enabled: boolean;
  toggle: () => void;
}

/**
 * Every event after which `isMicrophoneEnabled` may have changed. Muting fires
 * TrackMuted/TrackUnmuted; publishing and unpublishing (call start, call end)
 * fire the local-track pair; a reconnect can republish, which surfaces as a
 * connection-state change.
 */
const MIC_EVENTS = [RoomEvent.TrackMuted, RoomEvent.TrackUnmuted, RoomEvent.LocalTrackPublished, RoomEvent.LocalTrackUnpublished, RoomEvent.ConnectionStateChanged] as const;

/** Returns undefined without a Room — callers hide the control rather than showing a dead one. */
export const useMicrophone = (room: Room | undefined): MicrophoneControl | undefined => {
  const [enabled, setEnabled] = useState(() => room?.localParticipant.isMicrophoneEnabled ?? false);

  useEffect(() => {
    if (!room) return;

    const sync = (): void => setEnabled(room.localParticipant.isMicrophoneEnabled);
    // The mic may already be live when this mounts (the widget re-renders into
    // an ongoing call), so read once before subscribing.
    sync();

    MIC_EVENTS.forEach((event) => room.on(event, sync));
    return () => {
      MIC_EVENTS.forEach((event) => room.off(event, sync));
    };
  }, [room]);

  const toggle = useCallback(() => {
    if (!room) return;
    // Optimistic: LiveKit's mute round-trips through the native layer, and a
    // button that waits for it feels broken. The event listeners above correct
    // the state if the flip is rejected.
    const next = !room.localParticipant.isMicrophoneEnabled;
    setEnabled(next);
    void room.localParticipant.setMicrophoneEnabled(next).catch(() => setEnabled(!next));
  }, [room]);

  return room ? { enabled, toggle } : undefined;
};
